<script setup lang="ts">
import { nextTick, onScopeDispose, ref, computed, watch } from 'vue';
import { ArrowDownIcon, ArrowUpIcon, ArrowsUpDownIcon, ChevronDownIcon } from '@heroicons/vue/24/solid';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PagesPanel from '@/components/permissions/PagesPanel.vue';
import AbilityMultiSelect from '@/components/permissions/AbilityMultiSelect.vue';
import {
  type AbilityLevel,
  LEVEL_RANK,
  flagsForLevel,
  maxGrantableLevel,
} from '@/components/permissions/abilityFlagKey';
import {
  usePermissionUsers,
  usePermissionModels,
  useUserPermissions,
  useSavePermissions,
} from '@/composables/api/usePermissionsApi';
import { useAuthStore } from '@/stores/authStore';
import http from '@/services/http';
import { toast } from 'vue-sonner';
import type { ModelPermissions, PermissionModelEntry, PermissionRow } from '@/types/types';

const selectedUserId = ref<number | null>(null);
const selectedUserIdString = ref<string>('');

// The user picker collapses (collapsed by default) so the permission panels
// below get the vertical space — on mobile the card otherwise pushes the
// matrix off screen. The collapsed header names the selected user.
const selectUserSectionOpen = ref(false);
const copyFromUserIdString = ref<string>('');
const applyingCopyFromUser = ref(false);
const modelFilter = ref('');

const { data: usersData, isLoading: usersLoading } = usePermissionUsers();
const { data: modelsData, isLoading: modelsLoading } = usePermissionModels();
const { data: userPermissionsData, isLoading: permissionsLoading } = useUserPermissions(
  computed(() => selectedUserId.value)
);
const { mutate: savePermissions, isPending: saving } = useSavePermissions();

const selectedUserDisplay = computed(() => {
  const userEntry = usersData.value?.find(
    (entry) => String(entry.id) === selectedUserIdString.value
  );
  return userEntry ? `${userEntry.name} (${userEntry.email})` : null;
});

const permissionState = ref<Record<string, Record<string, boolean>>>({});
const isApplyingServerPermissions = ref(false);

const authStore = useAuthStore();

/**
 * Privilege bounding: the highest ability level the acting (authenticated)
 * user may grant for a given model. Developers are unbounded; everyone else is
 * capped at the level they themselves hold, mirroring the backend guard in
 * PermissionController::update. Prevents granting access the grantor lacks
 * (and hitting a 403 mid-save).
 */
function maxGrantableLevelForModel(modelKey: string): AbilityLevel {
  if (authStore.userData?.is_developer) {
    return 'delete';
  }
  const heldFlags = authStore.userData?.permissions?.[modelKey] ?? {};
  return maxGrantableLevel(heldFlags);
}

const abilities = [
  { key: 'can_view', label: 'View' },
  { key: 'can_create', label: 'Create' },
  { key: 'can_update', label: 'Edit' },
  { key: 'can_delete', label: 'Delete' },
] as const;

// Sort is now model-only — abilities are a dropdown per row, not five
// sortable columns. The cycleSort plumbing stays so the model header still
// flips asc / desc.
type SortColumn = 'model';

const sortColumn = ref<SortColumn>('model');
const sortDirection = ref<'asc' | 'desc'>('asc');

watch(selectedUserIdString, (newValue) => {
  if (newValue) {
    selectedUserId.value = parseInt(newValue, 10);
  } else {
    selectedUserId.value = null;
  }
});

// Auto-select the first user the moment the list arrives, but only if the
// admin hasn't already picked one — re-entering the page mid-edit must not
// trample an existing selection. The trigger is the array reference change,
// so manually clearing the dropdown back to '' won't fire this again.
watch(
  usersData,
  (newUsersData) => {
    if (selectedUserIdString.value) return;
    const firstUser = newUsersData?.[0];
    if (firstUser) {
      selectedUserIdString.value = String(firstUser.id);
    }
  },
  { immediate: true },
);

const flattenedModels = computed((): PermissionModelEntry[] => {
  if (!modelsData.value) return [];
  const list: PermissionModelEntry[] = [];
  for (const category of modelsData.value) {
    for (const modelEntry of category.models) {
      list.push(modelEntry);
    }
  }
  return list;
});

const filteredModels = computed(() => {
  const query = modelFilter.value.trim().toLowerCase();
  if (!query) return flattenedModels.value;
  return flattenedModels.value.filter(
    (modelEntry) =>
      modelEntry.display_name.toLowerCase().includes(query) ||
      modelEntry.key.toLowerCase().includes(query)
  );
});

const sortedFilteredModels = computed(() => {
  const rows = [...filteredModels.value];
  const column = sortColumn.value;
  const direction = sortDirection.value;
  const directionMultiplier = direction === 'asc' ? 1 : -1;

  rows.sort((modelA, modelB) => {
    let comparison = 0;
    if (column === 'model') {
      comparison = modelA.display_name.localeCompare(modelB.display_name, undefined, {
        sensitivity: 'base',
      });
      if (comparison === 0) {
        comparison = modelA.key.localeCompare(modelB.key);
      }
    }
    return comparison * directionMultiplier;
  });

  return rows;
});

function cycleSort(column: SortColumn) {
  if (sortColumn.value === column) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn.value = column;
    sortDirection.value = 'asc';
  }
}

function sortHeaderIcon(column: SortColumn) {
  if (sortColumn.value !== column) {
    return ArrowsUpDownIcon;
  }
  return sortDirection.value === 'asc' ? ArrowUpIcon : ArrowDownIcon;
}

function buildPermissionStateFromServerMap(
  serverPermissions: Record<string, ModelPermissions>,
): Record<string, Record<string, boolean>> {
  if (!modelsData.value) {
    return {};
  }
  const freshState: Record<string, Record<string, boolean>> = {};
  for (const category of modelsData.value) {
    for (const modelEntry of category.models) {
      const modelKey = modelEntry.key;
      const serverAbilities = serverPermissions[modelKey];
      freshState[modelKey] = {};
      for (const ability of abilities) {
        freshState[modelKey][ability.key] = serverAbilities?.[ability.key] ?? false;
      }
    }
  }
  return freshState;
}

watch(userPermissionsData, (serverPermissions) => {
  if (!serverPermissions || !modelsData.value) return;

  isApplyingServerPermissions.value = true;
  permissionState.value = buildPermissionStateFromServerMap(serverPermissions);
  nextTick(() => {
    isApplyingServerPermissions.value = false;
  });
});

function buildPermissionRows(): PermissionRow[] {
  const permissionRows: PermissionRow[] = [];
  for (const [modelKey, abilityMap] of Object.entries(permissionState.value)) {
    permissionRows.push({
      model: modelKey,
      can_view: abilityMap.can_view ?? false,
      can_create: abilityMap.can_create ?? false,
      can_update: abilityMap.can_update ?? false,
      can_delete: abilityMap.can_delete ?? false,
    });
  }
  return permissionRows;
}

const PERSIST_DEBOUNCE_MS = 400;
let persistDebounceHandle: ReturnType<typeof setTimeout> | null = null;

function cancelPersistDebounce() {
  if (persistDebounceHandle !== null) {
    clearTimeout(persistDebounceHandle);
    persistDebounceHandle = null;
  }
}

function schedulePersistPermissions() {
  cancelPersistDebounce();
  persistDebounceHandle = setTimeout(() => {
    persistDebounceHandle = null;
    if (
      selectedUserId.value === null
      || isApplyingServerPermissions.value
      || !modelsData.value
    ) {
      return;
    }
    savePermissions({
      userId: selectedUserId.value,
      permissions: buildPermissionRows(),
    });
  }, PERSIST_DEBOUNCE_MS);
}

watch(
  permissionState,
  () => {
    if (
      selectedUserId.value === null
      || isApplyingServerPermissions.value
      || !modelsData.value
    ) {
      return;
    }
    schedulePersistPermissions();
  },
  { deep: true }
);

watch(selectedUserId, () => {
  cancelPersistDebounce();
  copyFromUserIdString.value = '';
  modelFilter.value = '';
  sortColumn.value = 'model';
  sortDirection.value = 'asc';
});

const canApplyCopyFromUser = computed(() => {
  if (!copyFromUserIdString.value || selectedUserId.value === null) {
    return false;
  }
  const sourceId = parseInt(copyFromUserIdString.value, 10);
  return Number.isFinite(sourceId) && sourceId !== selectedUserId.value;
});

async function applyPermissionsFromAnotherUser() {
  if (!canApplyCopyFromUser.value || !modelsData.value) {
    return;
  }
  const sourceId = parseInt(copyFromUserIdString.value, 10);
  applyingCopyFromUser.value = true;
  try {
    const serverPermissions = await http
      .get<Record<string, ModelPermissions>>(`/api/permissions/${sourceId}`)
      .then((response) => response.data);
    permissionState.value = buildPermissionStateFromServerMap(serverPermissions);
  } catch {
    toast.error('Could not load permissions for that user.', { duration: 5000 });
  } finally {
    applyingCopyFromUser.value = false;
  }
}

onScopeDispose(() => {
  cancelPersistDebounce();
});

function ensureModelRow(modelKey: string) {
  if (!permissionState.value[modelKey]) {
    permissionState.value[modelKey] = {};
  }
}

/**
 * Apply a cumulative level pick from the AbilityMultiSelect dropdown. The
 * matrix mirrors the By-View editor's level semantics — the order is
 * none < view < edit < create < delete, so picking "Create" grants
 * View+Edit+Create and "Delete" grants all five. Edit sits below Create:
 * plain edit access does not imply create.
 */
function setAbilityLevelForModel(modelKey: string, level: AbilityLevel) {
  // Clamp to what the acting user may grant — the dropdown disables
  // out-of-bound levels, this is the matching guard for programmatic calls.
  const bound = maxGrantableLevelForModel(modelKey);
  if (LEVEL_RANK[level] > LEVEL_RANK[bound]) {
    return;
  }
  ensureModelRow(modelKey);
  const target = flagsForLevel(level);
  for (const ability of abilities) {
    permissionState.value[modelKey][ability.key] = target[ability.key];
  }
}

const isLoading = computed(() => usersLoading.value || modelsLoading.value);

// SAU-132: which surface is active. The "Pages" tab toggles per-user page
// (nav + route) access; the model matrix stays available as "By Model" for
// fine-grained model-permission edits.
const activeTab = ref<'pages' | 'models'>('pages');
</script>

<template>
  <!-- Outer wrapper fills the column area; inner column centers the content
       and caps it to a comfortable reading width (~Claude-chat sized) so
       the page doesn't sprawl across wide monitors. -->
  <div class="flex min-h-0 flex-1 flex-col p-6">
    <div class="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col gap-6">
    <div class="flex shrink-0 items-center gap-3">
      <h1 class="text-2xl font-semibold tracking-tight">Permissions</h1>
      <span v-if="selectedUserId !== null && saving" class="text-muted-foreground text-sm">
        Saving...
      </span>
    </div>

    <Card class="shrink-0 gap-0">
      <Collapsible v-model:open="selectUserSectionOpen">
        <CardHeader>
          <CollapsibleTrigger as-child>
            <button
              type="button"
              class="flex w-full items-center justify-between gap-2 text-left"
              data-testid="select-user-collapse-trigger"
              :aria-label="selectUserSectionOpen ? 'Collapse user selection' : 'Expand user selection'"
            >
              <!-- A user is always selected (auto-select on load), so the
                   collapsed header IS the user; "Select User" only shows
                   while expanded (or in the pre-load flash with no user). -->
              <CardTitle class="min-w-0 truncate" data-testid="select-user-card-title">
                {{ !selectUserSectionOpen && selectedUserDisplay ? selectedUserDisplay : 'Select User' }}
              </CardTitle>
              <ChevronDownIcon
                class="text-muted-foreground size-4 shrink-0 transition-transform"
                :class="selectUserSectionOpen ? 'rotate-180' : ''"
                aria-hidden="true"
              />
            </button>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent class="space-y-6 pt-6">
            <div v-if="isLoading" class="text-muted-foreground text-sm">Loading...</div>
            <template v-else>
              <div class="space-y-2">
                <p class="text-sm font-medium leading-none">User</p>
                <Select v-model="selectedUserIdString">
                  <SelectTrigger class="w-full max-w-md" aria-label="User to edit permissions for">
                    <SelectValue placeholder="Choose a user..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      v-for="userEntry in usersData"
                      :key="userEntry.id"
                      :value="String(userEntry.id)"
                    >
                      {{ userEntry.name }} ({{ userEntry.email }})
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div
                v-if="selectedUserId !== null"
                class="flex flex-col gap-3 border-t pt-6 sm:flex-row sm:items-end"
              >
                <div class="min-w-0 flex-1 space-y-2">
                  <p class="text-sm font-medium leading-none">Copy permissions from</p>
                  <p class="text-muted-foreground text-xs">
                    Replace the matrix below with another user&rsquo;s saved permissions (applies immediately; changes still auto-save).
                  </p>
                  <Select v-model="copyFromUserIdString">
                    <SelectTrigger class="w-full max-w-md" aria-label="Copy permissions from user">
                      <SelectValue placeholder="Choose a user to copy from..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem
                        v-for="userEntry in usersData"
                        :key="userEntry.id"
                        :value="String(userEntry.id)"
                      >
                        {{ userEntry.name }} ({{ userEntry.email }})
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  class="shrink-0"
                  :disabled="!canApplyCopyFromUser || applyingCopyFromUser"
                  @click="applyPermissionsFromAnotherUser"
                >
                  {{ applyingCopyFromUser ? 'Loading…' : 'Apply to selected user' }}
                </Button>
              </div>
            </template>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>

    <!-- Bottom section is meaningless without a target user — nothing to grant
         for, nothing to copy to. Suppress the Tabs entirely until one is
         picked; the auto-select on usersData arrival means this only flashes
         empty for the brief window before /api/permissions/users returns. -->
    <!-- Both tab panels share one card; the tab bar sits in the card header
         in place of the old per-panel "Pages" / "Models" titles it duplicated. -->
    <Tabs
      v-if="selectedUserId !== null"
      v-model="activeTab"
      class="flex min-h-0 w-full flex-1 flex-col"
    >
      <Card class="flex min-h-0 flex-1 flex-col gap-3">
        <CardHeader class="shrink-0">
          <TabsList data-testid="permissions-tab-list">
            <TabsTrigger value="pages" data-testid="permissions-tab-pages">Pages</TabsTrigger>
            <TabsTrigger value="models" data-testid="permissions-tab-models">By model</TabsTrigger>
          </TabsList>
        </CardHeader>

        <!-- Pages: per-user page (nav + route) access. Toggling a page on/off
             shows/hides the matching sidebar link for the user; it does NOT
             change their model permissions (that's the By-Model tab). -->
        <TabsContent value="pages" class="min-h-0 flex-1">
          <PagesPanel
            :selected-user-id="selectedUserId"
            :permission-state="permissionState"
            :max-grantable-level-for-model="maxGrantableLevelForModel"
            :set-ability-level-for-model="setAbilityLevelForModel"
          />
        </TabsContent>

        <!-- By Model: the pre-existing matrix. Untouched apart from being
             wrapped in the tab. -->
        <TabsContent value="models" class="min-h-0 flex-1">
          <div
            v-if="selectedUserId !== null && !permissionsLoading && modelsData"
            class="flex h-full min-h-0 flex-col"
          >
              <div class="shrink-0 border-b px-6 pb-4">
                <Input
                  v-model="modelFilter"
                  type="search"
                  placeholder="Filter by name or key..."
                  class="max-w-md"
                />
              </div>
              <div class="min-h-0 flex-1 overflow-y-auto max-md:pb-mobile-area-bar-clearance">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead class="min-w-[200px] max-w-[280px]">
                      <button
                        type="button"
                        class="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 font-medium"
                        @click="cycleSort('model')"
                      >
                        Model
                        <component :is="sortHeaderIcon('model')" class="size-4 shrink-0 opacity-70" />
                      </button>
                    </TableHead>
                    <TableHead class="text-muted-foreground text-sm font-medium">
                      Abilities
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow v-for="modelEntry in sortedFilteredModels" :key="modelEntry.key">
                    <TableCell class="font-medium">{{ modelEntry.display_name }}</TableCell>
                    <TableCell>
                      <AbilityMultiSelect
                        :flags="permissionState[modelEntry.key] ?? {}"
                        :max-level="maxGrantableLevelForModel(modelEntry.key)"
                        :test-id-prefix="`permissions-matrix-${modelEntry.key}-abilities`"
                        :aria-label="`Abilities for ${modelEntry.display_name}`"
                        :trigger-class="'w-56 justify-between'"
                        @set-level="(level) => setAbilityLevelForModel(modelEntry.key, level)"
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              </div>
          </div>

          <div
            v-else-if="selectedUserId !== null && permissionsLoading"
            class="text-muted-foreground px-6 text-sm"
          >
            Loading permissions...
          </div>
        </TabsContent>
      </Card>
    </Tabs>
    </div>
  </div>
</template>
